import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Mail, MessageSquareText } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

type CampaignDeliveryLogsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  campaignName: string;
};

type DeliveryLog = {
  id: string;
  type: "email" | "sms";
  status: string;
  recipient: string;
  detail: string;
  error_message?: string | null;
  created_at: string;
};

export function CampaignDeliveryLogsDialog({ open, onOpenChange, campaignId, campaignName }: CampaignDeliveryLogsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<DeliveryLog[]>([]);

  useEffect(() => {
    if (!open || !campaignId) return;
    void loadLogs();
  }, [open, campaignId]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const [emailRes, smsRes] = await Promise.all([
        supabase
          .from("campaign_email_sends")
          .select("id, status, subject, error_message, created_at, lead:leads(contact_name, contact_email, email), campaign_enrollments!inner(campaign_id)")
          .eq("campaign_enrollments.campaign_id", campaignId)
          .order("created_at", { ascending: false })
          .limit(50),
        (supabase as any)
          .from("campaign_sms_sends")
          .select("id, status, message, phone_number, error_message, created_at, lead:leads(contact_name), campaign_enrollments!inner(campaign_id)")
          .eq("campaign_enrollments.campaign_id", campaignId)
          .order("created_at", { ascending: false })
          .limit(50),
      ]);

      if (emailRes.error) throw emailRes.error;
      if (smsRes.error) throw smsRes.error;

      const emailLogs: DeliveryLog[] = (emailRes.data || []).map((row: any) => ({
        id: row.id,
        type: "email",
        status: row.status,
        recipient: row.lead?.contact_email || row.lead?.email || row.lead?.contact_name || "Unknown recipient",
        detail: row.subject || "Campaign email",
        error_message: row.error_message,
        created_at: row.created_at,
      }));

      const smsLogs: DeliveryLog[] = (smsRes.data || []).map((row: any) => ({
        id: row.id,
        type: "sms",
        status: row.status || "pending",
        recipient: row.phone_number || row.lead?.contact_name || "Unknown recipient",
        detail: row.message || "Campaign text",
        error_message: row.error_message,
        created_at: row.created_at,
      }));

      setLogs([...emailLogs, ...smsLogs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
    } catch (error: any) {
      toast.error(error.message || "Could not load delivery logs");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Delivery logs</DialogTitle>
          <DialogDescription>{campaignName}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[460px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No delivery attempts yet.</div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => {
                const Icon = log.type === "email" ? Mail : MessageSquareText;
                const failed = ["failed", "bounced"].includes(log.status);
                return (
                  <div key={`${log.type}-${log.id}`} className="rounded-md border p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate text-sm font-medium">{log.recipient}</span>
                          <Badge variant={failed ? "destructive" : "outline"} className="capitalize">{log.status}</Badge>
                        </div>
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{log.detail}</p>
                        {log.error_message && <p className="mt-2 flex gap-1 text-xs text-destructive"><AlertTriangle className="h-3 w-3 shrink-0" />{log.error_message}</p>}
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={loadLogs} disabled={loading}>Refresh</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}